import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import WorkGoalEditor from './WorkGoalEditor';
import { formatGoalElapsed, goalElapsedMs } from './goalElapsed';
import { macGoalAppearance } from './workGoalAppearance';

const labels = { active: 'Pursuing goal', paused: 'Goal paused', blocked: 'Goal needs input', complete: 'Goal complete' };
const reasons = {
  continuation_limit: 'The continuation limit was reached. Review progress, then resume to keep going.',
  waiting_on_user: 'Answer the pending request to continue.',
  orchestrator_restart: 'The worker restarted. Resume to continue from saved progress.',
  credits_exhausted: 'Add credits, then resume to continue.',
};

export default function WorkGoalBar({ goal, onSend, onControl, disabled = false }) {
  const { colors, theme } = useTheme();
  const isMac = Platform.OS === 'macos';
  const palette = isMac ? macGoalAppearance(theme) : null;
  const [expanded, setExpanded] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now);
  const inFlight = useRef(false);
  const locked = busy || disabled;
  useEffect(() => {
    setNow(Date.now());
    if (goal?.status !== 'active') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [goal?.id, goal?.status, goal?.activeSince]);
  useEffect(() => { setExpanded(false); setEditTarget(null); setError(''); }, [goal?.id]);
  if (!goal) return null;

  const run = async action => {
    if (locked || inFlight.current) return false;
    inFlight.current = true;
    setBusy(true); setError('');
    try {
      const accepted = await action();
      if (accepted === false) setError('The goal has not changed. Please try again.');
      return accepted;
    } catch (err) { setError(err?.message || 'Could not update the goal. Please try again.'); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const target = value => ({ goalId: value.id, goalRevision: value.revision });
  const iconButton = (label, icon, onPress, inactive = false) => (
    <Pressable accessibilityRole="button" accessibilityLabel={label}
      accessibilityState={{ disabled: locked || inactive }} disabled={locked || inactive}
      onPress={onPress} style={({ hovered, pressed }) => [styles.iconButton, isMac && macStyles.iconButton,
        isMac && (hovered || pressed) && { backgroundColor: palette.hover }, (locked || inactive) && styles.disabled]}>
      <Icon name={icon} size={14} color={palette?.secondary || colors.text_color_secondary} />
    </Pressable>
  );
  const toggleExpanded = () => { setExpanded(value => !value); setEditTarget(null); };
  return (
    <View testID="work-goal-bar" style={[styles.container, isMac && macStyles.container, {
      backgroundColor: palette?.surface || colors.solid_color_secondary, borderColor: palette?.border || colors.border_color_secondary,
    }]}>
      <View style={[styles.row, isMac && macStyles.row]}>
        <View style={isMac && [macStyles.symbol, { backgroundColor: palette.field }]}>
          <Icon name={isMac && goal.status === 'complete' ? 'check' : 'bullseye'} size={16} color={palette?.text || colors.text_color_secondary} />
        </View>
        <Pressable accessibilityRole="button" accessibilityLabel={`${labels[goal.status]}: ${goal.objective}. Show goal details`}
          accessibilityState={{ expanded, disabled: locked }} disabled={locked} onPress={toggleExpanded} style={styles.objective}>
          {isMac ? <View style={macStyles.heading}>
            <Text size="xs" weight="medium" style={[macStyles.status, { color: palette.secondary }]}>
              {labels[goal.status] || 'Goal'}
            </Text>
            <Text size="s" numberOfLines={1} ellipsizeMode="tail" style={[macStyles.objective, { color: palette.text }]}>
              {goal.objective}
            </Text>
          </View> : <Text size="s" numberOfLines={1} ellipsizeMode="tail">
            <Text size="s" weight="medium">{labels[goal.status] || 'Goal'} </Text>
            <Text size="s" color="secondary">{goal.objective}</Text>
          </Text>}
        </Pressable>
        <Text testID="goal-elapsed" size="xs" color="secondary" style={[styles.elapsed, isMac && [macStyles.elapsed, { color: palette.secondary }]]}>
          {formatGoalElapsed(goalElapsedMs(goal, now))}
        </Text>
        {!isMac && iconButton('Clear goal', 'trash', () => run(() => onControl('clear', goal)))}
        {goal.status === 'active' && iconButton('Pause goal', 'pause', () => run(() => onControl('pause', goal)))}
        {['paused', 'blocked'].includes(goal.status) && iconButton('Resume goal', 'play',
          () => run(() => onSend('/goal resume', [], target(goal))), goal.reason === 'waiting_on_user')}
        {iconButton(expanded ? 'Collapse goal' : 'Expand goal', isMac ? (expanded ? 'chevron-up' : 'chevron-down') : (expanded ? 'compress' : 'expand'), toggleExpanded)}
      </View>
      {expanded && (editTarget ? (
        <WorkGoalEditor initialObjective={editTarget.objective} editing embedded disabled={locked}
          onCancel={() => setEditTarget(null)} onSubmit={objective => run(async () => {
            const accepted = await onSend('/goal set ' + objective, [], target(editTarget));
            if (accepted !== false) setEditTarget(null);
            return accepted;
          })} />
      ) : <View style={[styles.details, isMac && [macStyles.details, { borderTopColor: palette.border }]]}>
        <Text size="s" selectable style={isMac && [macStyles.detailText, { color: palette.text }]}>{goal.objective}</Text>
        {!!(goal.summary || reasons[goal.reason]) && <Text size="xs" color="secondary" style={isMac && { color: palette.secondary }}>{reasons[goal.reason] || goal.summary}</Text>}
        <View style={macStyles.detailActions}>
          <Pressable accessibilityRole="button" accessibilityLabel="Edit goal" disabled={locked}
            accessibilityState={{ disabled: locked }}
            onPress={() => setEditTarget(goal)} style={({ hovered, pressed }) => [styles.editButton, isMac && macStyles.editButton,
              isMac && (hovered || pressed) && { backgroundColor: palette.hover }, locked && styles.disabled]}>
            <Text size="s" color="secondary" style={isMac && [macStyles.actionText, { color: palette.text }]}>Edit goal</Text>
          </Pressable>
          {isMac && iconButton('Clear goal', 'trash', () => run(() => onControl('clear', goal)))}
        </View>
      </View>)}
      {!!error && <View style={styles.details}><Text size="xs" accessibilityRole="alert">{error}</Text></View>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginHorizontal: 12, marginBottom: -14, paddingBottom: 14, borderWidth: 1,
    borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: 'hidden' },
  row: { minHeight: 44, flexDirection: 'row', alignItems: 'center', paddingLeft: 12, paddingRight: 4, gap: 6 },
  objective: { flex: 1, minWidth: 0, minHeight: 44, justifyContent: 'center' },
  elapsed: { flexShrink: 0, fontVariant: ['tabular-nums'] },
  iconButton: { width: 36, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  disabled: { opacity: 0.45 },
  details: { paddingHorizontal: 14, paddingBottom: 12, gap: 8 },
  editButton: { minHeight: 40, justifyContent: 'center', alignSelf: 'flex-start' },
});

const macStyles = StyleSheet.create({
  container: { marginHorizontal: 0, marginBottom: 10, paddingBottom: 0, borderRadius: 14 },
  row: { paddingHorizontal: 12, paddingVertical: 9, gap: 10 },
  symbol: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  heading: { gap: 3 },
  status: { fontFamily: 'System', fontSize: 11, lineHeight: 14 },
  objective: { fontFamily: 'System', fontSize: 13, lineHeight: 18 },
  elapsed: { fontFamily: 'System', fontSize: 11, marginHorizontal: 2 },
  iconButton: { width: 30, minHeight: 32, borderRadius: 7 },
  details: { padding: 16, gap: 12, borderTopWidth: 1 },
  detailText: { fontFamily: 'System', fontSize: 13, lineHeight: 20 },
  detailActions: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  editButton: { minHeight: 32, paddingHorizontal: 10, borderRadius: 7 },
  actionText: { fontFamily: 'System', fontSize: 12, lineHeight: 17 },
});
